import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

import { Calculation } from './calculation';
import { Progress } from './progress';
import { TargetHoverCard } from './target-hover-card';

// Dynamic grid: min-content for icons, auto for content to fit naturally
const gridTemplateColumns =
  'min-content auto min-content auto min-content auto';

interface TargetBudgetProps {
  allowExpand?: boolean;
}

export function TargetBudget({ allowExpand = true }: TargetBudgetProps) {
  const [isOpen, setIsOpen] = useState(true);
  const { formatCurrency } = useCurrencySymbol();
  const { hiringTarget, isFetching, isScheduled, isCompleted, campaign } =
    useEditCampaignContext();
  const { targetBudget } = hiringTarget;
  const isIntegratedChannel =
    campaign?.sourcing_channel.is_integratable ?? false;

  const toggleOpen = () => {
    if (!allowExpand || !isIntegratedChannel) return;
    setIsOpen(prev => !prev);
  };

  // Hide the target budget if the campaign is completed or scheduled
  if (isCompleted || isScheduled) return null;

  return (
    <div className="border-fx-brand-100 bg-fx-brand-50 flex flex-col gap-2 rounded-md border px-4 py-2">
      <button
        className="flex items-center justify-between"
        type="button"
        onClick={toggleOpen}
      >
        <div className="flex flex-col items-start gap-2">
          <Typography variant="subtitle1">
            <FormattedMessage
              defaultMessage="Hiring target budget"
              description="Expandable card title: $ cap for the current hire period"
            />
          </Typography>
          {isFetching ? (
            <Skeleton className="my-1 h-4 w-12" />
          ) : (
            <Typography variant="h6">{formatCurrency(targetBudget)}</Typography>
          )}
        </div>
        {(isIntegratedChannel || !allowExpand) && (
          <>
            {isOpen ? (
              <ChevronDownIcon className="text-fx-primary h-4 w-4" />
            ) : (
              <ChevronRightIcon className="text-fx-primary h-4 w-4" />
            )}
          </>
        )}
      </button>

      {isOpen && (
        <>
          {isFetching ? (
            <Skeleton className="h-11 w-full" />
          ) : (
            <div
              className="grid w-full min-w-0 gap-4"
              style={{
                gridTemplateColumns,
              }}
            >
              <Calculation />
            </div>
          )}
        </>
      )}

      <TargetHoverCard isFetching={isFetching}>
        <Progress isFetching={isFetching} />
      </TargetHoverCard>
    </div>
  );
}
